import type { DiscoveryCandidate } from "./types";
import type { FriendshipLane } from "@/lib/profile/types";

/**
 * DiscoveryFilters — client-side refinements applied on top of the
 * server-side fn_discover_candidates page. The RPC already enforces
 * app/lane/radius/match-state. These filters narrow the returned page
 * for surface UX without round-tripping.
 *
 * `maxKm` is the only filter that re-queries the server (it's an RPC arg).
 */
export interface DiscoveryFilters {
  maxKm: number;
  lane: FriendshipLane | "any";
  verifiedOnly: boolean;
  withVoiceIntro: boolean;
  promptKeyword: string;
}

export const DEFAULT_FILTERS: DiscoveryFilters = {
  maxKm: 80,
  lane: "any",
  verifiedOnly: false,
  withVoiceIntro: false,
  promptKeyword: "",
};

export const FILTERS_STORAGE_KEY = "wavlynq.discovery.filters.v1";

export const isDefaultFilters = (f: DiscoveryFilters): boolean =>
  f.maxKm === DEFAULT_FILTERS.maxKm &&
  f.lane === DEFAULT_FILTERS.lane &&
  f.verifiedOnly === DEFAULT_FILTERS.verifiedOnly &&
  f.withVoiceIntro === DEFAULT_FILTERS.withVoiceIntro &&
  f.promptKeyword.trim() === "";

export const filtersDelta = (f: DiscoveryFilters): Record<string, unknown> => {
  const delta: Record<string, unknown> = {};
  if (f.maxKm !== DEFAULT_FILTERS.maxKm) delta.max_km = f.maxKm;
  if (f.lane !== DEFAULT_FILTERS.lane) delta.lane = f.lane;
  if (f.verifiedOnly) delta.verified_only = true;
  if (f.withVoiceIntro) delta.with_voice_intro = true;
  const kw = f.promptKeyword.trim();
  if (kw) delta.prompt_keyword_len = kw.length;
  return delta;
};

export const applyClientFilters = (
  rows: DiscoveryCandidate[],
  f: DiscoveryFilters,
): DiscoveryCandidate[] => {
  const kw = f.promptKeyword.trim().toLowerCase();
  return rows.filter((c) => {
    if (f.lane !== "any" && c.friendship_lane !== "both" && c.friendship_lane !== f.lane) {
      return false;
    }
    if (f.verifiedOnly && !(c.bgc_verified || c.community_verified)) return false;
    if (f.withVoiceIntro && !c.voice_intro_url) return false;
    if (kw) {
      const hay = (c.prompts ?? [])
        .map((p) => `${p.text ?? ""} ${p.answer ?? ""}`)
        .join(" \n ")
        .toLowerCase();
      if (!hay.includes(kw)) return false;
    }
    return true;
  });
};

export const loadFilters = (): DiscoveryFilters => {
  if (typeof window === "undefined") return DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<DiscoveryFilters>;
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch {
    return DEFAULT_FILTERS;
  }
};

export const saveFilters = (f: DiscoveryFilters): void => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f));
  } catch {
    /* quota — ignore */
  }
};
